import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { Activity, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_DOT = {
    ok: "bg-[#56CCF2]",
    configured: "bg-[#56CCF2]",
    unconfigured: "bg-[#F4B731]",
    unavailable: "bg-[#FB7185]",
    error: "bg-[#FB7185]",
    degraded: "bg-[#F4B731]",
    unknown: "bg-[#94A3B8]",
};

const STATUS_LABEL = {
    ok: "OPERATIONAL",
    configured: "CONFIGURED",
    unconfigured: "NOT CONFIGURED",
    unavailable: "UNAVAILABLE",
    error: "ERROR",
    degraded: "DEGRADED",
    unknown: "UNKNOWN",
};

function statusKey(v) {
    if (!v) return "unknown";
    const s = String(v).toLowerCase();
    if (STATUS_DOT[s]) return s;
    if (s.startsWith("error")) return "error";
    return "unknown";
}

function StatusRow({ label, value, description }) {
    const k = statusKey(value);
    return (
        <li className="px-6 py-4 grid grid-cols-12 gap-4 items-center">
            <div className="col-span-5">
                <div className="text-sm text-white font-medium">{label}</div>
                {description && <div className="text-xs text-[#64748B] mt-0.5">{description}</div>}
            </div>
            <div className="col-span-7 flex items-center gap-2.5">
                <span className={`w-2 h-2 rounded-full ${STATUS_DOT[k]}`} />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#CBD5E1] font-medium">
                    {STATUS_LABEL[k] || String(value).toUpperCase()}
                </span>
            </div>
        </li>
    );
}

function EnvRow({ name, present, critical }) {
    return (
        <li className="px-6 py-3 grid grid-cols-12 gap-4 items-center text-xs font-mono">
            <div className="col-span-8 text-[#CBD5E1] truncate">{name}</div>
            <div className="col-span-4 flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${present ? "bg-[#56CCF2]" : critical ? "bg-[#FB7185]" : "bg-[#F4B731]"}`} />
                <span className={`text-[10px] uppercase tracking-[0.18em] ${present ? "text-[#56CCF2]" : critical ? "text-[#FB7185]" : "text-[#F4B731]"}`}>
                    {present ? "set" : "unset"}
                </span>
            </div>
        </li>
    );
}

// Production-critical secrets (red dot if unset)
const CRITICAL_ENV = new Set([
    "JWT_SECRET", "PREVIEW_KEY", "ADMIN_EMAIL", "ADMIN_PASSWORD",
    "CORS_ORIGINS", "MONGO_URL", "DB_NAME",
]);

// Launch-critical but not boot-critical (amber if unset)
const LAUNCH_ENV = new Set([
    "PUBLIC_SITE_URL", "RESEND_API_KEY", "RESEND_FROM_EMAIL",
    "SHOPIFY_WEBHOOK_SECRET", "SHOPIFY_STORE_DOMAIN",
]);

export default function AdminHealth() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/health")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load system health"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <AdminLayout>
                <div className="p-10 text-sm text-[#64748B]">Probing systems…</div>
            </AdminLayout>
        );
    }

    if (!data) return null;

    const s = data.subsystems || {};
    const m = data.metrics || {};
    const env = data.env_present || {};

    return (
        <AdminLayout>
            <div className="p-8 sm:p-10 max-w-5xl">
                <header className="mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-3">
                        <Activity className="w-3.5 h-3.5" />
                        Operations · System
                    </div>
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-white tracking-tight">System health</h1>
                            <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
                                Authoritative status for every subsystem TCP depends on. Environment-variable presence is shown as booleans only — values never leave the server.
                            </p>
                        </div>
                        <button
                            data-testid="health-refresh"
                            onClick={load}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs uppercase tracking-[0.18em] text-[#94A3B8] hover:text-white border border-[#243044] hover:border-[#2F80ED] transition-colors"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                            Re-probe
                        </button>
                    </div>
                </header>

                {/* App envelope */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <Tile label="Overall" value={STATUS_LABEL[statusKey(data.status)] || data.status} dotKey={statusKey(data.status)} />
                    <Tile label="Environment" value={(data.app_env || "preview").toUpperCase()} dotKey={data.app_env === "production" ? "ok" : "unknown"} />
                    <Tile label="Build" value={data.version || "—"} dotKey="ok" />
                </div>

                {/* Subsystems */}
                <section className="mb-10">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3">Subsystems</h2>
                    <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden">
                        <ul className="divide-y divide-[#243044]" data-testid="health-subsystems">
                            <StatusRow label="MongoDB" value={s.mongo} description="User records, topics, magic-link tokens, webhook log" />
                            <StatusRow label="Object storage" value={s.storage} description="Featured images and uploaded documents" />
                            <StatusRow label="Resend (transactional email)" value={s.resend} description="Magic-link delivery" />
                            <StatusRow label="Shopify webhook ingest" value={s.shopify_webhook} description="Membership sync via HMAC-verified webhooks" />
                        </ul>
                    </div>
                </section>

                {/* Live metrics */}
                <section className="mb-10">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3">Live signals</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Tile label="Webhooks · 7d" value={m.webhooks_total_7d ?? 0} dotKey="ok" />
                        <Tile
                            label="Failed webhooks · 24h"
                            value={m.webhooks_failed_24h ?? 0}
                            dotKey={(m.webhooks_failed_24h ?? 0) > 0 ? "error" : "ok"}
                        />
                        <Tile
                            label="Auth failures · 15m"
                            value={m.auth_failures_15m ?? 0}
                            dotKey={(m.auth_failures_15m ?? 0) >= 5 ? "error" : (m.auth_failures_15m ?? 0) > 0 ? "degraded" : "ok"}
                        />
                    </div>
                    {m.last_webhook && (
                        <div className="mt-3 text-xs text-[#64748B] flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Last webhook: <span className="text-[#94A3B8] font-mono">{m.last_webhook.topic}</span>
                            <span className="text-[#64748B]">·</span>
                            <span className="text-[#94A3B8]">{m.last_webhook.status}</span>
                            <span className="text-[#64748B]">·</span>
                            <span className="text-[#94A3B8]">{new Date(m.last_webhook.received_at).toLocaleString()}</span>
                        </div>
                    )}
                </section>

                {/* Env presence */}
                <section>
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3 flex items-center gap-2">
                        Environment variables
                        <span className="text-[10px] normal-case tracking-normal text-[#64748B] italic">— presence only · values never sent to the browser</span>
                    </h2>
                    <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden">
                        <ul className="divide-y divide-[#243044]" data-testid="health-env">
                            {Object.entries(env).map(([key, present]) => (
                                <EnvRow
                                    key={key}
                                    name={key}
                                    present={!!present}
                                    critical={CRITICAL_ENV.has(key)}
                                />
                            ))}
                        </ul>
                    </div>
                    <div className="mt-3 text-xs text-[#64748B] italic flex items-start gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                            Red = required for boot. Amber = required for full launch (webhook ingest, magic-link delivery, canonical URLs).
                        </span>
                    </div>
                </section>
            </div>
        </AdminLayout>
    );
}

function Tile({ label, value, dotKey }) {
    return (
        <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
            <div className="flex items-center gap-2.5 text-xs uppercase tracking-[0.18em] text-[#64748B] mb-3">
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[dotKey] || STATUS_DOT.unknown}`} />
                {label}
            </div>
            <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
    );
}
